/**
 * Section panel. Screen spec 4.10.
 *
 * The bordered section the product screens are built from: a heading, at most
 * one control in the head, and the section's one thing underneath. The head is
 * a hairline separated strip rather than a heavier band, so a page of these
 * reads as a ledger of sections and not as a wall of cards.
 *
 * It enforces the "one thing per section" discipline by shape: there is one
 * slot for an action, and a screen that wants two has to make a second section
 * and name it, which is the argument it should have been having anyway.
 *
 * The heading is a real heading element, at a level the caller sets, because a
 * screen reader user navigating by heading is the fastest reader this product
 * has and the overview is a page they will land on constantly.
 */

import type { ReactNode } from 'react';
import '../../styles/components/section-panel.css';

export interface SectionPanelProps {
  title: string;
  /** Heading level. The overview's H1 is the project name, so these are H2s. */
  level?: 2 | 3;
  /** One control, right aligned in the head. Never the screen's ink block. */
  action?: ReactNode;
  /** One line under the head. The consequence, or what the section is for. */
  note?: string;
  children: ReactNode;
  /** Removes the body padding, for a section whose body is its own ledger. */
  flush?: boolean;
}

export function SectionPanel({
  title,
  level = 2,
  action,
  note,
  children,
  flush = false,
}: SectionPanelProps) {
  const Heading = level === 2 ? 'h2' : 'h3';

  return (
    <section className="sect" data-flush={flush ? 'yes' : 'no'}>
      <div className="sect__head">
        <Heading className="sect__title">{title}</Heading>
        {action ? <div className="sect__action">{action}</div> : null}
      </div>
      {note ? <p className="sect__note">{note}</p> : null}
      <div className="sect__body">{children}</div>
    </section>
  );
}
